import React, { useEffect, useRef } from "react";
import ReceiptPrintLayout from "../ReceiptPrintLayout";
import {
  formatReceiptDateTime,
  isReceiptEditableToday,
} from "../../utils/receiptHistory";

const formatCurrency = (value) =>
  Math.round(Number(value) || 0).toLocaleString("en-US");

function containDialogFocus(event, container, onClose) {
  if (event.key === "Escape") {
    event.preventDefault();
    onClose();
    return;
  }
  if (event.key !== "Tab") return;

  const focusable = Array.from(
    container?.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    ) || [],
  );
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (!first) {
    event.preventDefault();
  } else if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

export default function ReceiptDetailDialog({
  receipt,
  onClose,
  onPrint,
  onEdit,
}) {
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const returnFocusRef = useRef(null);
  const canEdit = isReceiptEditableToday(receipt?.createdAt);

  useEffect(() => {
    returnFocusRef.current = document.activeElement;
    closeButtonRef.current?.focus();
    return () => returnFocusRef.current?.focus?.();
  }, []);

  if (!receipt) return null;

  return (
    <div className="modal-overlay receipt-detail-overlay">
      <div
        ref={dialogRef}
        className="modal modal-lg receipt-detail-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="receipt-detail-title"
        onKeyDown={(event) => containDialogFocus(event, dialogRef.current, onClose)}
      >
        <div className="modal-header">
          <div>
            <p className="eyebrow">RECEIPT DETAILS</p>
            <h2 id="receipt-detail-title">{receipt.transactionId}</h2>
            <p>{formatReceiptDateTime(receipt.createdAt)}</p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="close-btn"
            onClick={onClose}
            aria-label="Close receipt details"
          >
            &times;
          </button>
        </div>

        <div className="receipt-detail-body">
          <dl className="receipt-detail-summary">
            <div>
              <dt>Total</dt>
              <dd>MMK {formatCurrency(receipt.totalAmount)}</dd>
            </div>
            <div>
              <dt>Items</dt>
              <dd>{receipt.items.length}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>{receipt.corrected ? "Corrected" : "Completed"}</dd>
            </div>
          </dl>
          <ReceiptPrintLayout receipt={receipt} formatCurrency={formatCurrency} />
        </div>

        <div className="modal-footer receipt-detail-actions">
          <button
            type="button"
            className="btn btn-secondary"
            data-print-receipt
            onClick={onPrint}
          >
            Reprint receipt
          </button>
          {canEdit && (
            <button
              type="button"
              className="btn btn-primary"
              data-edit-receipt
              onClick={() => onEdit(receipt)}
            >
              Edit receipt
            </button>
          )}
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
